// components/SquarePaymentProcessor.jsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import "./payment.css";
import "../App.css";
import URL from "../Redux/Url";

const SquarePaymentProcessor = ({
  appId,
  locationId,
  order,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const { savedCard } = useSelector((state) => state.savedCard);

  const [sdkReady, setSdkReady]         = useState(false);
  const [payments, setPayments]         = useState(null);
  const [card, setCard]                 = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [useSavedCard, setUseSavedCard] = useState(false);
  const [saveCard, setSaveCard]         = useState(false);

  // ── Resolve guest vs logged-in user info ─────────────────────────────────
  const isGuestOrder = !order?.user;

  // guestCustomer may arrive as a populated object or a bare ID string.
  // Only treat it as an object if it has real name/email fields on it.
  const guestCustomerObj =
    order?.guestCustomer && typeof order.guestCustomer === "object"
      ? order.guestCustomer
      : null;

  const customerName =
    order?.user?.name             ||
    guestCustomerObj?.displayName ||
    order?.guestName              ||
    "Guest";

  const customerEmail =
    order?.user?.email      ||
    guestCustomerObj?.email ||
    order?.guestEmail       ||
    "";

  const mongoUserId = order?.user?._id ?? order?.user ?? null;

  // Surface a clear console error early rather than letting Square fail silently
  useEffect(() => {
    if (isGuestOrder && !customerEmail) {
      console.error(
        "[SquarePaymentProcessor] Guest order has no resolvable email. " +
        "Make sure the order is fetched with .populate('guestCustomer').",
        { guestCustomer: order?.guestCustomer, guestEmail: order?.guestEmail }
      );
    }
  }, [isGuestOrder, customerEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Guests never have a saved card — only auto-select saved card for logged-in users
    if (savedCard && !isGuestOrder) setUseSavedCard(true);
  }, [savedCard, isGuestOrder]);

  // ── Initialize Square SDK ────────────────────────────────────────────────
  useEffect(() => {
    const initializeSquare = async () => {
      try {
        if (!window.Square) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://web.squarecdn.com/v1/square.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }
        const paymentsInstance = window.Square.payments(appId, locationId);
        setPayments(paymentsInstance);
        setSdkReady(true);
      } catch (error) {
        console.error("Error initializing Square:", error);
        setErrorMessage("Failed to initialize payment system");
      }
    };

    initializeSquare();

    return () => {
      if (card) card.destroy().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, locationId]);

  // ── Mount card form only when entering a new card ────────────────────────
  useEffect(() => {
    if (!sdkReady || !payments || card || useSavedCard) return;

    const initializeCard = async () => {
      try {
        const cardContainer = document.getElementById("card-container");
        if (cardContainer) {
          const initializedCard = await payments.card();
          await initializedCard.attach("#card-container");
          setCard(initializedCard);
        }
      } catch (error) {
        console.error("Error initializing card:", error);
        setErrorMessage("Failed to initialize card payment");
      }
    };

    initializeCard();
  }, [sdkReady, payments, card, useSavedCard]);

  const handleSwitchToSaved = () => {
    if (card) {
      card.destroy().catch(() => {});
      setCard(null);
    }
    setUseSavedCard(true);
    setSaveCard(false);
  };

  const handleSwitchToNew = () => {
    setUseSavedCard(false);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const tokenize = async (cardInstance) => {
    const tokenResult = await cardInstance.tokenize();
    if (tokenResult.status === "OK") return tokenResult.token;
    throw new Error(`Tokenization failed: ${tokenResult.status}`);
  };

  const verifyBuyer = async (paymentsInstance, token, orderData) => {
    const verificationDetails = {
      amount: orderData.totalPrice.toFixed(2),
      billingContact: {
        givenName:    customerName,
        email:        customerEmail,
        addressLines: [orderData.shippingAddress.street1],
        city:         orderData.shippingAddress.city,
        state:        orderData.shippingAddress.state,
        countryCode:  orderData.shippingAddress.country,
      },
      currencyCode: "USD",
      intent: "CHARGE",
    };
    const result = await paymentsInstance.verifyBuyer(token, verificationDetails);
    return result.token;
  };

  // ── Send to backend with auth token when available ───────────────────────
  const sendPaymentToBackend = async (payload) => {
    const userInfo   = JSON.parse(localStorage.getItem("userInfo") || "null");
    const guestToken = sessionStorage.getItem("guestOrderToken");
    const token      = userInfo?.token || guestToken || null;

    const response = await fetch(`${URL}/api/payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || "Payment request failed");
    }
    return response.json();
  };

  // ── Process payment ──────────────────────────────────────────────────────
  const processPayment = async () => {
    setIsProcessing(true);
    setErrorMessage("");

    // Guard: guest orders must have a resolvable email before we even try
    if (isGuestOrder && !customerEmail) {
      setErrorMessage("Unable to resolve guest email. Please contact support.");
      setIsProcessing(false);
      return;
    }

    try {
      const amountInCents = order ? Math.round(order.totalPrice * 100) : 0;

      const shippingAddress = {
        address_line_1:                  order.shippingAddress.street1,
        locality:                        order.shippingAddress.city,
        administrative_district_level_1: order.shippingAddress.state,
        postal_code:                     order.shippingAddress.zipcode,
        country:                         order.shippingAddress.country,
        first_name:                      customerName,
      };

      if (useSavedCard && savedCard?.squareCardId) {
        const result = await sendPaymentToBackend({
          locationId,
          sourceId:       savedCard.squareCardId,
          idempotencyKey: window.crypto.randomUUID(),
          amount:         amountInCents,
          email:          customerEmail,
          givenName:      customerName,
          order,
          cardOnFile:     true,
          saveCard:       false,
          userId:         mongoUserId,
          isGuestOrder,
          shippingAddress,
        });

        onPaymentSuccess(result);
      } else {
        if (!card) throw new Error("Card payment is not properly initialized");

        const token             = await tokenize(card);
        const verificationToken = await verifyBuyer(payments, token, order);

        const result = await sendPaymentToBackend({
          locationId,
          sourceId:         token,
          verificationToken,
          idempotencyKey:   window.crypto.randomUUID(),
          amount:           amountInCents,
          email:            customerEmail,
          givenName:        customerName,
          order,
          cardOnFile:       false,
          saveCard:         isGuestOrder ? false : saveCard,
          userId:           mongoUserId,
          isGuestOrder,
          shippingAddress,
        });

        onPaymentSuccess(result);
      }
    } catch (error) {
      console.error("Payment processing error:", error);
      setErrorMessage(error.message || "Payment failed. Please try again.");
      onPaymentError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Brand icon helper ────────────────────────────────────────────────────
  const brandIconClass = {
    VISA:       "fa-cc-visa",
    MASTERCARD: "fa-cc-mastercard",
    AMEX:       "fa-cc-amex",
    DISCOVER:   "fa-cc-discover",
  };

  // Block Pay button if guest email is unresolvable
  const isPayDisabled =
    isProcessing                       ||
    !sdkReady                          ||
    (!useSavedCard && !card)           ||
    (isGuestOrder && !customerEmail);

  const hasDiscount = order?.discountCode && order?.discountAmount > 0;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="square-payment-processor">
      {errorMessage && (
        <div className="payment-error">{errorMessage}</div>
      )}

      {/* ── Saved card toggle — only for logged-in users ─────────────── */}
      {savedCard && !isGuestOrder && (
        <div className="saved-card-section">
          <div
            className={`saved-card-option ${useSavedCard ? "active" : ""}`}
            onClick={handleSwitchToSaved}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleSwitchToSaved()}
          >
            <div className="saved-card-radio">
              <input
                type="radio"
                id="use-saved-card"
                checked={useSavedCard}
                onChange={handleSwitchToSaved}
              />
            </div>
            <label htmlFor="use-saved-card" className="saved-card-label">
              <i
                className={`fab ${
                  brandIconClass[savedCard.cardBrand] || "fas fa-credit-card"
                } saved-card-brand-icon`}
              ></i>
              <div className="saved-card-info">
                <span className="saved-card-number">
                  {savedCard.cardBrand} •••• {savedCard.last4}
                </span>
                <span className="saved-card-tag">Saved card</span>
              </div>
            </label>
          </div>

          <div
            className={`new-card-option ${!useSavedCard ? "active" : ""}`}
            onClick={handleSwitchToNew}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleSwitchToNew()}
          >
            <div className="saved-card-radio">
              <input
                type="radio"
                id="use-new-card"
                checked={!useSavedCard}
                onChange={handleSwitchToNew}
              />
            </div>
            <label htmlFor="use-new-card" className="saved-card-label">
              <i className="fas fa-plus-circle saved-card-brand-icon"></i>
              <span>Use a different card</span>
            </label>
          </div>
        </div>
      )}

      {/* ── Card entry form ──────────────────────────────────────────── */}
      {!useSavedCard && (
        <div className="card-payment-container">
          <div id="card-container" className="square-card-container"></div>

          {!isGuestOrder && (
            <div className="save-card-checkbox">
              <input
                type="checkbox"
                id="save-card"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
              />
              <label htmlFor="save-card">
                <i className="fas fa-lock"></i>
                Save card for future purchases
              </label>
            </div>
          )}
        </div>
      )}

      {/* ── Discount summary ─────────────────────────────────────────── */}
      {hasDiscount && (
        <div
          style={{
            display:        "flex",
            justifyContent: "space-between",
            alignItems:     "center",
            background:     "#f0fdf4",
            border:         "1px solid #bbf7d0",
            borderRadius:   8,
            padding:        "10px 14px",
            marginBottom:   12,
            fontSize:       13,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }}>🏷️</span>
            <div>
              <div style={{ fontWeight: 700, color: "#166534" }}>
                Discount applied
              </div>
              <div
                style={{
                  fontFamily:    "monospace",
                  fontSize:      12,
                  color:         "#16a34a",
                  letterSpacing: "0.06em",
                  fontWeight:    600,
                }}
              >
                {order.discountCode}
              </div>
            </div>
          </div>
          <div style={{ fontWeight: 800, color: "#166534", fontSize: 15 }}>
            −${Number(order.discountAmount).toFixed(2)}
          </div>
        </div>
      )}

      <button
        className="payment-button"
        disabled={isPayDisabled}
        onClick={processPayment}
      >
        {isProcessing
          ? "Processing..."
          : `Pay $${order ? order.totalPrice.toFixed(2) : "0.00"}`}
      </button>
    </div>
  );
};

SquarePaymentProcessor.propTypes = {
  appId:            PropTypes.string.isRequired,
  locationId:       PropTypes.string.isRequired,
  order:            PropTypes.object.isRequired,
  onPaymentSuccess: PropTypes.func.isRequired,
  onPaymentError:   PropTypes.func.isRequired,
};

export default SquarePaymentProcessor;